import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { 
  DRIVER_APPROVAL_REQUEST_CREATE, 
  DRIVER_APPROVAL_REQUEST_UPDATE,
  INSURANCE_POLICIES_LIST 
} from '../../graphql/insurancePolicies';
import { EMPLOYEE_LIST } from '../../graphql/employees';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { Email as EmailIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuthContext } from '../../sections/auth';
import EmailService from '../../services/emailService';
import { EMAIL_TEMPLATE_GET_BY_USE_CASE } from '../../graphql/emailTemplates';
import { EMAIL_LOG_CREATE } from '../../graphql/emailLogs';
import { handleDriverApprovalWorkflow } from '../../utils/emailWorkflow';

const DriverApprovalRequestForm = ({ open, onClose, request = null, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    employeeId: '',
    insurancePolicyId: '',
    requestReason: '',
    status: 'pending'
  });

  // Email template data
  const [emailData, setEmailData] = useState({
    subject: '',
    body: '',
    recipients: [],
    attachments: []
  });

  // Email connection status
  const [emailConnected, setEmailConnected] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailType, setEmailType] = useState('gmail'); // or 'outlook'

  // Auth context
  const { user } = useAuthContext();
  
  // Queries
  const { data: employeesData, loading: employeesLoading } = useQuery(EMPLOYEE_LIST);
  const { data: policiesData, loading: policiesLoading } = useQuery(INSURANCE_POLICIES_LIST);
  const { data: emailTemplatesData, loading: emailTemplatesLoading } = useQuery(
    EMAIL_TEMPLATE_GET_BY_USE_CASE,
    { variables: { category: 'driver_approval' } }
  );

  // Mutations
  const [createRequest, { loading: createLoading }] = useMutation(DRIVER_APPROVAL_REQUEST_CREATE);
  const [updateRequest, { loading: updateLoading }] = useMutation(DRIVER_APPROVAL_REQUEST_UPDATE);
  const [createEmailLog] = useMutation(EMAIL_LOG_CREATE);

  useEffect(() => {
    if (request) {
      setFormData({
        employeeId: request.employee?.id || '',
        insurancePolicyId: request.insurancePolicy?.id || '',
        requestReason: request.requestReason || '',
        status: request.status || 'pending'
      });
      setEmailSent(!!request.emailSentDate);
    } else {
      setFormData({
        employeeId: '',
        insurancePolicyId: '',
        requestReason: '',
        status: 'pending'
      });
      setEmailSent(false);
    }
    setActiveStep(0);
  }, [request]);

  // Check email connection status
  useEffect(() => {
    const checkEmailConnection = async () => {
      const gmailToken = localStorage.getItem("gmail_token");
      const outlookToken = localStorage.getItem("outlook_access_token");
      
      if (gmailToken) {
        // For now, just check if token exists and has proper format
        // We'll validate it when actually sending emails
        if (gmailToken.length > 50) { // Basic validation
          setEmailConnected(true);
          setEmailType('gmail');
        } else {
          console.log('Gmail token seems invalid (too short)');
          setEmailConnected(false);
        }
      } else if (outlookToken) {
        setEmailConnected(true);
        setEmailType('outlook');
      } else {
        setEmailConnected(false);
        console.log('No email tokens found');
      }
    };

    checkEmailConnection();
  }, []);

  const steps = ['Select Employee & Policy', 'Review & Send Email', 'Complete Request'];

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate step 1
      if (!formData.employeeId || !formData.insurancePolicyId) {
        enqueueSnackbar('Please select both employee and insurance policy', { variant: 'error' });
        return;
      }
      
      // Load email template
      loadEmailTemplate();
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const loadEmailTemplate = () => {
    const selectedEmployee = employeesData?.employeesList?.items?.find(emp => emp.id === formData.employeeId);
    const selectedPolicy = policiesData?.insurancePoliciesList?.items?.find(policy => policy.id === formData.insurancePolicyId);

    if (selectedEmployee && selectedPolicy) {
      // Get the first available template from 8base for driver_approval
      const availableTemplates = emailTemplatesData?.emailTemplatesList?.items?.filter(template => 
        template.isActive && template.category === 'driver_approval'
      ) || [];

      if (availableTemplates.length > 0) {
        // Use the first available template from 8base
        const template = availableTemplates[0];
        // Process the template with dynamic variables
        const processedSubject = processTemplate(template.subject, selectedEmployee, selectedPolicy);
        const processedBody = processTemplate(template.body, selectedEmployee, selectedPolicy); 

        const emailTemplate = {
          subject: processedSubject,
          body: processedBody,
          recipients: [selectedPolicy.agentEmail || 'xopes49335@noidem.com'],
          attachments: selectedEmployee.driverLicensePhoto?.items?.length > 0 ? selectedEmployee.driverLicensePhoto.items : []
        };

        setEmailData(emailTemplate);
      } else {
        // Fallback to a basic template if no 8base template is found
        const fallbackTemplate = {
          subject: `Driver Approval Request - ${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
          body: `Dear Insurance Agent,

We are requesting approval to add the following employee to our insurance policy:

**Policy Information:**
- Policy Number: {{policyNumber}}
- Insurance Provider: {{provider}}
- Business Unit: {{businessUnitName}}

**Employee Information:**
- Name: {{employeeName}}
- Position: {{employeePosition}}

**Request Details:**
- Requested By: {{requestedByName}}
- Request Date: {{currentDate}}

Please review this request and provide approval or any additional requirements needed.

If you have any questions, please contact us.

Best regards,
{{requestedByName}}
{{businessUnitName}}`,
          recipients: [selectedPolicy.agentEmail || 'xopes49335@noidem.com'],
          attachments: selectedEmployee.driverLicensePhoto?.items?.length > 0 ? selectedEmployee.driverLicensePhoto.items : []
        };

        setEmailData(fallbackTemplate);
      }
    } else {
      console.log('❌ Employee or Policy not found:');
      console.log('  Selected Employee ID:', formData.employeeId);
      console.log('  Selected Policy ID:', formData.insurancePolicyId);
      console.log('  Available Employees:', employeesData?.employeesList?.items?.length);
      console.log('  Available Policies:', policiesData?.insurancePoliciesList?.items?.length);
      console.log('  Employees loading:', employeesLoading);
      console.log('  Policies loading:', policiesLoading);
      
      // If data is still loading, wait a bit and try again
      if (employeesLoading || policiesLoading) {
        console.log('⏳ Data still loading, will retry in 1 second...');
        setTimeout(() => {
          loadEmailTemplate();
        }, 1000);
      }
    }
  };

  // Process template with dynamic variables
  const processTemplate = (template, selectedEmployee, selectedPolicy) => {
    if (!template) return '';
    
    const currentDate = dayjs().format('YYYY-MM-DD');
    
    let processedTemplate = template;
    
    // Replace all variables with more flexible data access
    const replacements = [
      { pattern: /\{\{policyNumber\}\}/g, value: selectedPolicy?.policyNumber || selectedPolicy?.policy_number || 'N/A' },
      { pattern: /\{\{provider\}\}/g, value: selectedPolicy?.provider || selectedPolicy?.insuranceProvider || 'N/A' },
      { pattern: /\{\{employeeName\}\}/g, value: selectedEmployee ? `${selectedEmployee.firstName || selectedEmployee.first_name || ''} ${selectedEmployee.lastName || selectedEmployee.last_name || ''}`.trim() : 'N/A' },
      { pattern: /\{\{employeePosition\}\}/g, value: selectedEmployee?.position || selectedEmployee?.jobTitle || 'Employee' },
      { pattern: /\{\{businessUnitName\}\}/g, value: selectedPolicy?.businessUnits?.name || selectedPolicy?.businessUnit?.name || selectedPolicy?.business_unit?.name || 'N/A' },
      { pattern: /\{\{requestedByName\}\}/g, value: user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() : 'User' },
      { pattern: /\{\{currentDate\}\}/g, value: currentDate },
      { pattern: /\{\{agentName\}\}/g, value: selectedPolicy?.agentName || selectedPolicy?.agent_name || 'Insurance Agent' },
      { pattern: /\{\{agentEmail\}\}/g, value: selectedPolicy?.agentEmail || selectedPolicy?.agent_email || 'xopes49335@noidem.com' },
      { pattern: /\{\{requestReason\}\}/g, value: formData.requestReason || 'Driver approval request' },
      { pattern: /\{\{employeeEmail\}\}/g, value: selectedEmployee?.emailAddress || selectedEmployee?.email || 'N/A' },
      { pattern: /\{\{employeeId\}\}/g, value: selectedEmployee?.id || 'N/A' },
      { pattern: /\{\{coverageType\}\}/g, value: selectedPolicy?.coverageType || selectedPolicy?.coverage_type || 'N/A' },
      { pattern: /\{\{effectiveDate\}\}/g, value: selectedPolicy?.effectiveDate || selectedPolicy?.effective_date || currentDate },
      { pattern: /\{\{expirationDate\}\}/g, value: selectedPolicy?.expirationDate || selectedPolicy?.expiration_date || 'N/A' }
    ];
    
    replacements.forEach(({ pattern, value }) => {
      const beforeReplace = processedTemplate;
      processedTemplate = processedTemplate.replace(pattern, value);
      if (beforeReplace !== processedTemplate) {
        console.log(`  ✅ Replaced ${pattern} with: ${value}`);
      } else {
        console.log(`  ❌ No replacement for ${pattern} - value: ${value}`);
      }
    });
    
    console.log('  Final processed template:', processedTemplate);
    return processedTemplate;
  };

  const handleSendEmail = async () => {
    if (!emailConnected) {
      enqueueSnackbar('Please connect your email account first from settings', { variant: 'error' });
      return;
    }

    setEmailLoading(true);
    
    try {
      const selectedEmployee = employeesData?.employeesList?.items?.find(emp => emp.id === formData.employeeId);
      const selectedPolicy = policiesData?.insurancePoliciesList?.items?.find(policy => policy.id === formData.insurancePolicyId);
      
      // Create the driver request first
      const today = dayjs().format('YYYY-MM-DD');
      const requestData = {
        insurancePolicy: { connect: { id: selectedPolicy.id } },
        employee: { connect: { id: selectedEmployee.id } },
        requestDate: today,
        status: 'pending',
        emailSentDate: today
      };

      console.log('Creating driver request:', requestData);
      const requestResult = await createRequest({
        variables: { data: requestData }
      });
      
      const createdRequest = requestResult?.data?.driverApprovalRequestCreate;
      console.log('✅ Driver request created:', createdRequest);

      if (!createdRequest) {
        throw new Error('Failed to create request - no data returned');
      }

      // Get the 8base template
      const availableTemplates = emailTemplatesData?.emailTemplatesList?.items?.filter(template => 
        template.isActive && template.category === 'driver_approval'
      ) || [];

      if (availableTemplates.length > 0) {
        const template = availableTemplates[0];
        console.log('📧 Using 8base template for email:', template);

        // Use the new email workflow system
        const workflowResult = await handleDriverApprovalWorkflow(
          createdRequest, 
          template, 
          user, 
          createEmailLog,
          selectedPolicy // Pass policy data
        );

        console.log('📧 Workflow result:', workflowResult);
        
        if (workflowResult.emailSent) {
          setEmailSent(true);
          enqueueSnackbar('Email sent successfully via Gmail!', { variant: 'success' });
        } else if (workflowResult.error) {
          enqueueSnackbar(`Request created but email failed: ${workflowResult.error}`, { variant: 'warning' });
        }
      } else {
        enqueueSnackbar('No email template found for driver approval', { variant: 'warning' });
      }
      
      handleNext();
    } catch (error) {
      console.error('Email sending error:', error);
      enqueueSnackbar(`Error sending email: ${error.message}`, { variant: 'error' });
    } finally {
      setEmailLoading(false);
    }
  };

  const sendGmailEmail = async (formData, token) => {
    const message = [
      "To: " + formData.to,
      "Subject: " + formData.subject,
      "",
      formData.message,
    ].join("\n");

    console.log('Sending Gmail email...');
    console.log('Message:', message);

    try {
      // First, let's test the Gmail API connection
      console.log('Testing Gmail API connection...');
      const profileResponse = await axios.get(
        'https://gmail.googleapis.com/gmail/v1/users/me/profile',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log('Gmail profile test successful:', profileResponse.data);

      // Now send the email
      console.log('Sending email via Gmail API...');
      const emailResponse = await axios.post(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/send`,
        {
          raw: btoa(message),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      // Check if it's a 401 Unauthorized error
      if (error.response?.status === 401) {
        console.log('401 Unauthorized - attempting token refresh...');
        if (localStorage.getItem("google_refresh_token")) {
          try {
            // Try to refresh token
            const refreshResponse = await axios.post(
              `https://oauth2.googleapis.com/token`,
              {
                client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
                refresh_token: localStorage.getItem("google_refresh_token"),
                grant_type: "refresh_token",
              },
              {
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                },
              }
            );
            
            if (refreshResponse.data?.access_token) {
              console.log('Token refreshed successfully');
              localStorage.setItem("google_access_token", refreshResponse.data.access_token);
              // Retry with new token
              await sendGmailEmail(formData, refreshResponse.data.access_token);
              return;
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            throw new Error("Gmail authentication failed. Please reconnect your Gmail account from settings.");
          }
        } else {
          throw new Error("Gmail access token expired. Please reconnect your Gmail account from settings.");
        }
      }
      
      // Handle other errors
      if (error.response?.status === 403) {
        throw new Error("Gmail API access denied. Please check your Gmail API permissions.");
      }
      
      throw new Error(`Gmail API error: ${error.response?.data?.error?.message || error.message}`);
    }
  };

  const sendOutlookEmail = async (formData, token) => {
    const payload = {
      message: {
        subject: formData.subject,
        body: {
          contentType: "Text",
          content: formData.message,
        },
        toRecipients: [
          {
            emailAddress: {
              address: formData.to,
            },
          },
        ],
      },
    };

    try {
      await axios.post(
        "https://graph.microsoft.com/v1.0/me/sendMail",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      throw new Error("Please configure your Outlook account from settings!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Format dates properly for 8base (YYYY-MM-DD format)
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const variables = {
        data: {
          employee: { connect: { id: formData.employeeId } },
          insurancePolicy: { connect: { id: formData.insurancePolicyId } },
          status: formData.status,
          requestDate: formattedDate,
          emailSentDate: emailSent ? formattedDate : null
        }
      };

      let createdRequest;

      if (request) {
        // Update existing request
        await updateRequest({
          variables: {
            filter: { id: { equals: request.id } },
            data: variables.data
          }
        });
        enqueueSnackbar('Driver approval request updated successfully', { variant: 'success' });
      } else {
        // Create new request
        const result = await createRequest({ variables });
        createdRequest = result.data.driverApprovalRequestCreate;
        enqueueSnackbar('Driver approval request created successfully', { variant: 'success' });
      }

      // Send email if Gmail is configured and we have a template
      if (emailConnected && emailTemplatesData?.emailTemplatesList?.items?.length > 0) {
        try {
          const emailService = new EmailService();
          const template = emailTemplatesData.emailTemplatesList.items[0]; // Use first template
          const toEmail = selectedEmployee?.emailAddress || selectedEmployee?.email;
          
          if (toEmail) {
            const variables = emailService.getDriverApprovalVariables(
              formData,
              selectedEmployee,
              selectedPolicy,
              user
            );
            
            await emailService.sendEmailWithTemplate(template, toEmail, variables);
            enqueueSnackbar('Email sent successfully', { variant: 'success' });
          } else {
            enqueueSnackbar('Employee email not found', { variant: 'warning' });
          }
        } catch (emailError) {
          console.error('Email sending failed:', emailError);
          enqueueSnackbar(`Email sending failed: ${emailError.message}`, { variant: 'warning' });
        }
      } else if (!emailConnected) {
        enqueueSnackbar('Gmail not configured. Email not sent.', { variant: 'warning' });
      } else {
        enqueueSnackbar('No email template found for driver approval.', { variant: 'warning' });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const employees = employeesData?.employeesList?.items || [];
  const policies = policiesData?.insurancePoliciesList?.items || [];

  const selectedEmployee = employees.find(emp => emp.id === formData.employeeId);
  const selectedPolicy = policies.find(policy => policy.id === formData.insurancePolicyId);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Select an employee and insurance policy to request driver approval. 
                Only employees from the same business unit as the policy will be available.
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={formData.employeeId}
                  onChange={(e) => handleChange('employeeId', e.target.value)}
                  disabled={employeesLoading}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName} - {employee.emailAddress}
                      {employee.businessUnit && ` (${employee.businessUnit.name})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Insurance Policy</InputLabel>
                <Select
                  value={formData.insurancePolicyId}
                  onChange={(e) => handleChange('insurancePolicyId', e.target.value)}
                  disabled={policiesLoading}
                >
                  {policies.map((policy) => (
                    <MenuItem key={policy.id} value={policy.id}>
                      {policy.policyNumber} - {policy.provider}
                      {policy.businessUnit && ` (${policy.businessUnit.name})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Request Reason"
                value={formData.requestReason}
                onChange={(e) => handleChange('requestReason', e.target.value)}
                placeholder="Explain why this driver approval is needed..."
              />
            </Grid>

            {selectedEmployee && selectedPolicy && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Validation Check</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {selectedEmployee.businessUnits?.id === selectedPolicy.businessUnits?.id ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <WarningIcon color="warning" />
                      )}
                      <Typography>
                        Business Unit Match: {selectedEmployee.businessUnits?.id === selectedPolicy.businessUnits?.id ? 'Valid' : 'Warning - Different Business Units'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {selectedEmployee.driverLicensePhoto?.items?.length > 0 ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <WarningIcon color="warning" />
                      )}
                      <Typography>
                        Driver's License Photo: {selectedEmployee.driverLicensePhoto?.items?.length > 0 ? 'Available' : 'Missing - Please upload'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Review the email that will be sent to the insurance agent. You can edit the content before sending.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Email Preview</Typography>
                  
                  {/* Template Selection */}
                  {emailTemplatesData?.emailTemplatesList?.items?.filter(t => t.isActive && t.category === 'driver_approval').length > 1 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>Template:</Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={emailTemplatesData?.emailTemplatesList?.items?.find(t => t.subject === emailData.subject)?.id || ''}
                          onChange={(e) => {
                            const template = emailTemplatesData.emailTemplatesList.items.find(t => t.id === e.target.value);
                            if (template) {
                              const selectedEmployee = employeesData?.employeesList?.items?.find(emp => emp.id === formData.employeeId);
                              const selectedPolicy = policiesData?.insurancePoliciesList?.items?.find(policy => policy.id === formData.insurancePolicyId);
                              
                              console.log('🔄 Template changed to:', template.name);
                              console.log('🔄 Selected Employee:', selectedEmployee);
                              console.log('🔄 Selected Policy:', selectedPolicy);
                              
                              const processedSubject = processTemplate(template.subject, selectedEmployee, selectedPolicy);
                              const processedBody = processTemplate(template.body, selectedEmployee, selectedPolicy);
                              
                              setEmailData(prev => ({
                                ...prev,
                                subject: processedSubject,
                                body: processedBody
                              }));
                            }
                          }}
                        >
                          {emailTemplatesData.emailTemplatesList.items
                            .filter(t => t.isActive && t.category === 'driver_approval')
                            .map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">To:</Typography>
                    <Typography>{emailData.recipients.join(', ')}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Subject:</Typography>
                    <TextField
                      fullWidth
                      value={emailData.subject}
                      onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Body:</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      value={emailData.body}
                      onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
                    />
                  </Box>

                  {emailData.attachments.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Attachments:</Typography>
                      {emailData.attachments.map((attachment, index) => (
                        <Chip key={index} label={attachment.name || 'Driver License Photo'} size="small" sx={{ mr: 1 }} />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

                         <Grid item xs={12}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   {emailConnected ? (
                     <CheckCircleIcon color="success" />
                   ) : (
                     <WarningIcon color="error" />
                   )}
                   <Typography>
                     Email Connection: {emailConnected ? `${emailType.toUpperCase()} Connected` : 'Not Connected'}
                   </Typography>
                 </Box>
                 {!emailConnected && (
                   <Button 
                     size="small" 
                     variant="outlined" 
                     onClick={() => window.location.href = '/settings'}
                   >
                     Configure Email
                   </Button>
                 )}
               </Box>
             </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Email sent successfully! The request has been created and is now pending approval from the insurance agent.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Request Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Employee:</Typography>
                      <Typography>{selectedEmployee?.firstName} {selectedEmployee?.lastName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Policy:</Typography>
                      <Typography>{selectedPolicy?.policyNumber}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Request Date:</Typography>
                      <Typography>{dayjs().format('MMM DD, YYYY')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Email Sent:</Typography>
                      <Typography>{emailSent ? 'Yes' : 'No'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {request && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Request Information:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Request Date: ${dayjs(request.requestDate).format('MMM DD, YYYY')}`} 
                      size="small" 
                    />
                    <Chip 
                      label={`Requested By: ${request.requestedBy?.firstName} ${request.requestedBy?.lastName}`} 
                      size="small" 
                    />
                    {request.emailSentDate && (
                      <Chip 
                        label={`Email Sent: ${dayjs(request.emailSentDate).format('MMM DD, YYYY')}`} 
                        size="small" 
                        color="success"
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {request ? 'Edit Driver Approval Request' : 'New Driver Approval Request'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        
                 {activeStep === 1 && (
           <Button 
             onClick={handleSendEmail}
             variant="contained"
             startIcon={emailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
             disabled={!emailConnected || emailLoading}
           >
             {emailLoading ? 'Sending...' : `Send Email via ${emailType.toUpperCase()}`}
           </Button>
         )}
        
        {activeStep < steps.length - 1 && activeStep !== 1 && (
          <Button 
            onClick={handleNext}
            variant="contained"
            disabled={!formData.employeeId || !formData.insurancePolicyId}
          >
            Next
          </Button>
        )}
        
        {activeStep === steps.length - 1 && (
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={createLoading || updateLoading}
          >
            {createLoading || updateLoading ? (
              <CircularProgress size={20} />
            ) : (
              request ? 'Update Request' : 'Create Request'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DriverApprovalRequestForm;
